import {
  indoorCategoriesAtom,
  indoorItemNamesAtom,
  indoorItemOffersAtom,
  indoorMenuAtom,
  indoorServiceAtom,
} from "@/atoms/indoor";
import { deriveServiceMenuData } from "@/lib/derive-service-menu-data";
import { ApiData } from "@/types";
import { useSetAtom } from "jotai";
import { useHydrateAtoms } from "jotai/utils";
import { useEffect, useMemo, useRef } from "react";

/**
 * Parallel hydration for the INDOOR service (QR menu).
 * Independent of ApiHydration (DELIVERY) — they coexist as siblings in
 * ApiCache. Renders children regardless of INDOOR availability; the /menu
 * page detects missing indoor data and shows its own empty state.
 */
export default function IndoorApiHydration({
  data,
  children,
}: {
  data: ApiData;
  children: React.ReactNode;
}) {
  const derived = useMemo(() => deriveServiceMenuData(data, "INDOOR"), [data]);
  const isInitialMount = useRef(true);

  const setIndoorService = useSetAtom(indoorServiceAtom);
  const setIndoorMenu = useSetAtom(indoorMenuAtom);
  const setIndoorCategories = useSetAtom(indoorCategoriesAtom);
  const setIndoorItemOffers = useSetAtom(indoorItemOffersAtom);
  const setIndoorItemNames = useSetAtom(indoorItemNamesAtom);

  useHydrateAtoms([
    [indoorServiceAtom, derived?.service ?? null],
    [indoorMenuAtom, derived?.menu ?? null],
    [indoorCategoriesAtom, derived?.categories ?? []],
    [indoorItemOffersAtom, derived?.itemOffers ?? []],
    [indoorItemNamesAtom, derived?.itemNames ?? {}],
  ]);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    setIndoorService(derived?.service ?? null);
    setIndoorMenu(derived?.menu ?? null);
    setIndoorCategories(derived?.categories ?? []);
    setIndoorItemOffers(derived?.itemOffers ?? []);
    setIndoorItemNames(derived?.itemNames ?? {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  return children;
}
